import Button from 'components/Button';
import MentorStatusChip from 'components/Chips/MentorStatusChip';
import getFilters from 'components/Filters';
import List from 'components/list';
import { BoldTd, Td } from 'components/list/Td';
import { Th } from 'components/list/Th';
import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { setPage, setSearch, setTake } from 'store/actions/admin/actions';
import { StoreState } from 'store/reducers';
import useSWR, { mutate } from 'swr';
import { Action } from 'types/typeguards/reducerActions';
import User from 'types/User';
import privateAPI from 'utils/privateAxiosInstance';

type ValidateTrainingButtonProps = {
  id: number;
  mutateList: () => void;
};
const ValidateTrainingButton: FC<ValidateTrainingButtonProps> = ({
  id,
  mutateList,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const handleClick = () => {
    setLoading(true);
    privateAPI
      .patch(`/user/status/${id}`, {
        mentorStatus: 'ACCEPTED',
      })
      .then(() => {
        setLoading(false);
        mutate('/user/mentors');
        mutate(`/user/${id}`);
        mutateList();
      })
      .finally(() => setLoading(false));
  };

  return (
    <Button
      type="button"
      color="primary"
      onClick={handleClick}
      loading={loading}
    >
      Valider la formation
    </Button>
  );
};

const Filters = getFilters<never>();

const columnNames = ['firstName', 'lastName', 'status', 'validateBtn'] as const;
type ColumnName = typeof columnNames[number];
const activeColumns: Record<ColumnName, boolean> = {
  firstName: true,
  lastName: true,
  status: true,
  validateBtn: true,
};

type RetrievedData = {
  mentors: User[] | undefined;
  pages: number | undefined;
};

type Props = {
  page: number;
  setPage: (page: number) => void;
  search: string;
  setSearch: (search: string) => void;
  take: number;
  setTake: (take: number) => void;
};

const MentorFormationActions: FC<Props> = ({
  page,
  setPage,
  search,
  setSearch,
  take,
  setTake,
}) => {
  const {
    data,
    error,
    mutate: mutateList,
  } = useSWR<RetrievedData>(
    `/user/notification/admin/mentor-formation?page=${page}&take=${take}${
      search ? `&search=${search}` : ''
    }`,
  );

  const [retrievedData, setRetrievedData] = useState<RetrievedData>({
    mentors: undefined,
    pages: undefined,
  });
  useEffect(() => {
    if (data) {
      setRetrievedData(data);
    }
  }, [data]);

  return (
    <>
      <Filters
        tagList={[]}
        setTags={() => null}
        setSearch={setSearch}
        state={{ tags: [], search, take }}
        setTake={setTake}
      />
      <List
        isEmpty={!retrievedData.mentors?.[0]}
        emptyText="Il n'y a aucune action à effectuer pour les formations mentor"
        pages={retrievedData.pages}
        page={page}
        setPage={setPage}
        loading={!data && !error}
        columnNumber={4}
        activeColumns={activeColumns}
      >
        <Th name="firstName">Prénom</Th>
        <Th name="lastName">Nom</Th>
        <Th name="status">Statut</Th>
        <Th name="validateBtn" />
        {retrievedData.mentors?.map((mentor, idx) => (
          <React.Fragment key={idx}>
            <BoldTd name="firstName">
              <Link to={`/admin/mentoring/mentors/${mentor?.id}/training/`}>
                {mentor.firstName}
              </Link>
            </BoldTd>
            <BoldTd name="lastName">
              <Link to={`/admin/mentoring/mentors/${mentor.id}/training/`}>
                {mentor.lastName}
              </Link>
            </BoldTd>
            <Td name="status">
              {mentor.mentorStatus && (
                <MentorStatusChip status={mentor.mentorStatus} />
              )}
            </Td>
            <Td name="validateBtn">
              <ValidateTrainingButton
                id={mentor.id || -1}
                mutateList={mutateList}
              />
            </Td>
          </React.Fragment>
        ))}
      </List>
    </>
  );
};

const mapStateToProps = (state: StoreState) => {
  return {
    page: state.admin.actions.mentorFormation.page,
    search: state.admin.actions.mentorFormation.search,
    take: state.admin.actions.mentorFormation.take,
  };
};

const mapDispatchToProps = (dispatch: (action: Action) => void) => {
  return {
    setPage: (page: number) => dispatch(setPage('mentorFormation', page)),
    setSearch: (search: string) =>
      dispatch(setSearch('mentorFormation', search)),
    setTake: (take: number) => dispatch(setTake('mentorFormation', take)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MentorFormationActions);
